import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Edit } from 'lucide-react';

type Fornecedor = {
  id: string;
  codigo_fornecedor: string;
  cnpj: string;
  razao_social: string;
  apelido: string | null;
  inscricao_estadual: string | null;
  inscricao_municipal: string | null;
  email_nfe: string | null;
  fora_municipio: boolean | null;
  ibge_municipio: string | null;
  limite_credito: number | null;
  matricula_cei_cno: string | null;
  agencia: string | null;
  conta: string | null;
  pix: string | null;
  banco: string | null;
  cnpj_bancario: string | null;
  ativo: boolean;
  created_at: string;
  updated_at: string;
};

type Local = {
  id: string;
  nome: string;
  logradouro: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
};

type Contato = {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  cargo: string;
};

export default function FornecedorView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [fornecedor, setFornecedor] = useState<Fornecedor | null>(null);
  const [locais, setLocais] = useState<Local[]>([]);
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [loading, setLoading] = useState(true);

  const canRead = hasPermission('clientes', 'fornecedores', 'read');
  const canWrite = hasPermission('clientes', 'fornecedores', 'write');

  useEffect(() => {
    if (id && canRead) {
      loadFornecedor();
    }
  }, [id, canRead]);

  const loadFornecedor = async () => {
    if (!id) return;

    try {
      // Carregar dados do fornecedor
      const { data: fornecedorData, error: fornecedorError } = await supabase
        .from('fornecedores')
        .select('*')
        .eq('id', id)
        .single();

      if (fornecedorError) throw fornecedorError;
      setFornecedor({ ...fornecedorData, ativo: fornecedorData.status === 'ativo' });

      // Carregar locais relacionados
      const { data: locaisData, error: locaisError } = await supabase
        .from('locais')
        .select('*')
        .eq('id_fornecedor', id);

      if (locaisError) throw locaisError;
      setLocais(locaisData || []);

      // Carregar contatos relacionados
      const { data: contatosData, error: contatosError } = await supabase
        .from('contatos')
        .select('*')
        .eq('id_fornecedor', id);

      if (contatosError) throw contatosError;
      setContatos(contatosData || []);

    } catch (error: any) {
      console.error('Erro ao carregar fornecedor:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do fornecedor: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!canRead) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Você não tem permissão para visualizar fornecedores.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!fornecedor) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Fornecedor não encontrado.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/intranet/fornecedores')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{fornecedor.razao_social}</h1>
            <p className="text-muted-foreground">
              Código: {fornecedor.codigo_fornecedor}
            </p>
          </div>
        </div>
        {canWrite && (
          <Button onClick={() => navigate(`/intranet/fornecedores/${id}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </Button>
        )}
      </div>

      <Tabs defaultValue="dados-basicos" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
          <TabsTrigger value="endereco">Endereços</TabsTrigger>
          <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
          <TabsTrigger value="contatos">Contatos</TabsTrigger>
          <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
        </TabsList>

        <TabsContent value="dados-basicos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Dados Básicos</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">Código</h4>
                <p>{fornecedor.codigo_fornecedor}</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">CNPJ</h4>
                <p>{fornecedor.cnpj}</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">Razão Social</h4>
                <p>{fornecedor.razao_social}</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">Apelido</h4>
                <p>{fornecedor.apelido || '-'}</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">Status</h4>
                <Badge variant={fornecedor.ativo ? "default" : "secondary"}>
                  {fornecedor.ativo ? "Ativo" : "Inativo"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="endereco" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Endereços Vinculados</CardTitle>
            </CardHeader>
            <CardContent>
              {locais.length > 0 ? (
                <div className="space-y-4">
                  {locais.map((local) => (
                    <div key={local.id} className="border rounded-lg p-4">
                      <h4 className="font-semibold">{local.nome}</h4>
                      <p className="text-muted-foreground">
                        {local.logradouro}, {local.numero}
                      </p>
                      <p className="text-muted-foreground">
                        {local.bairro} - {local.cidade}/{local.estado}
                      </p>
                      <p className="text-muted-foreground">CEP: {local.cep}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">Nenhum endereço vinculado.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fiscal" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informações Fiscais</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">Inscrição Estadual</h4>
                <p>{fornecedor.inscricao_estadual || '-'}</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">Inscrição Municipal</h4>
                <p>{fornecedor.inscricao_municipal || '-'}</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">E-mail NFe</h4>
                <p>{fornecedor.email_nfe || '-'}</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">IBGE Município</h4>
                <p>{fornecedor.ibge_municipio || '-'}</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">Fora do Município</h4>
                <Badge variant={fornecedor.fora_municipio ? "default" : "secondary"}>
                  {fornecedor.fora_municipio ? "Sim" : "Não"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contatos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Contatos Associados</CardTitle>
            </CardHeader>
            <CardContent>
              {contatos.length > 0 ? (
                <div className="space-y-4">
                  {contatos.map((contato) => (
                    <div key={contato.id} className="border rounded-lg p-4">
                      <h4 className="font-semibold">{contato.nome}</h4>
                      {contato.cargo && (
                        <p className="text-muted-foreground">{contato.cargo}</p>
                      )}
                      <p className="text-muted-foreground">{contato.email}</p>
                      <p className="text-muted-foreground">{contato.telefone}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">Nenhum contato associado.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="financeiro" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informações Financeiras</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground">Limite de Crédito</h4>
                  <p>
                    {fornecedor.limite_credito 
                      ? `R$ ${fornecedor.limite_credito.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` 
                      : '-'
                    }
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground">Matrícula CEI/CNO</h4>
                  <p>{fornecedor.matricula_cei_cno || '-'}</p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold">Dados Bancários de Recebimento</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4">
                  <div>
                    <h5 className="font-semibold text-sm text-muted-foreground">Banco</h5>
                    <p>{fornecedor.banco || '-'}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-sm text-muted-foreground">Agência</h5>
                    <p>{fornecedor.agencia || '-'}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-sm text-muted-foreground">Conta</h5>
                    <p>{fornecedor.conta || '-'}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-sm text-muted-foreground">CNPJ</h5>
                    <p>{fornecedor.cnpj_bancario || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <h5 className="font-semibold text-sm text-muted-foreground">PIX</h5>
                    <p>{fornecedor.pix || '-'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}